import React, { Component } from 'react'
import { Modal, ModalHeader, ModalBody } from 'reactstrap'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import BlockWithLoader from '../../../Support/BlockWithLoader'
import { API_BASE_URL } from '../../../../utils/apiConfig'

const WPS_QUERY = gql`
  query WpsFetchQuery($wps_id: ID!) {
    wps(wps_id: $wps_id) {
      id
      no
      rev_no
      manufacturer
      release_date
      actual
      additional_info
      pqr_wpqr_wpqt
      welding_processes_names
      joint_types_names
      welding_positions_names
      weld_types_names
      preparations_names
      metal_transfers_names
      shield_gases_names
      backing_gases_names
      welding_details_names
      shield_gas_flow_rate
      backing_gas_flow_rate
      gouging
      bevel
      backing
      preheat_temp
      post_weld_heat_operate
      interpass_temp
      post_heating
      preheat_maintenenace_temp
      standard { id name }
      used_base_materials {
        id
        drawing_symbol
        base_material_designation_name
        thickness
        diameter
        base_material { id name group subgroup standard }
        product_type { id name }
      }
      joint_design { id url }
      welding_sequence { id url }
      attachments { id data_file_name url }
      parameters {
        id
        run
        filler_material {
          id
          name
          standard { id name description }
        }
        filler_material_operate
        filler_material_size
        welding_process { id name }
        welding_positions { id name }
        current
        voltage
        wire_feed
        travel_speed
        type_of_current_polarity { id name }
        heat_input
      }
    }
  }
`

const s = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '12px' },
  th: { background: '#f0f0f0', padding: '4px 6px', border: '1px solid #ccc', whiteSpace: 'nowrap' },
  td: { padding: '4px 6px', border: '1px solid #ccc' },
  section: { fontWeight: 'bold', marginBottom: '4px', marginTop: '12px', fontSize: '13px' },
  sectionToggle: { fontWeight: 'bold', marginBottom: '4px', marginTop: '12px', fontSize: '13px', cursor: 'pointer', userSelect: 'none', color: '#20a8d8' },
  row: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' },
  cell: { flex: '1 1 140px', fontSize: '12px' },
  label: { color: '#666', fontSize: '11px' },
  val: { fontWeight: '600' },
}

function Field({ label, value }) {
  if (!value && value !== 0) return null
  return (
    <div style={s.cell}>
      <div style={s.label}>{label}</div>
      <div style={s.val}>{value}</div>
    </div>
  )
}

function formatRange(range) {
  if (!range || range.length !== 2) return '-'
  return `${parseFloat(range[0]).toFixed(1)} - ${parseFloat(range[1]).toFixed(1)}`
}

function formatArrayRange(array) {
  if (!array || array.length !== 2) return '-'
  const fmt = v => (v === '∞' || v === 'Infinity') ? '∞' : parseFloat(v).toFixed(2)
  return `${fmt(array[0])} - ${fmt(array[1])}`
}

function parseFillerMaterial(name) {
  if (!name) return { material: '', classification: '', standard: '' }
  const parts = name.split(' - ')
  return { material: parts[0] || '', classification: parts[1] || '', standard: parts[2] || '' }
}

class WpsViewModal extends Component {
  render() {
    const { wpsId, onClose } = this.props
    return (
      <Modal isOpen={true} toggle={onClose} size="xl" scrollable>
        <ModalHeader toggle={onClose}>WPS Details</ModalHeader>
        <ModalBody>
          <Query query={WPS_QUERY} variables={{ wps_id: String(wpsId) }}>
            {({ loading, error, data }) => (
              <BlockWithLoader loading={loading} renderChildren={true}>
                {error && <div>Error: {error.message}</div>}
                {data && data.wps && <WpsContent wps={data.wps} />}
              </BlockWithLoader>
            )}
          </Query>
        </ModalBody>
      </Modal>
    )
  }
}

class WpsContent extends Component {
  constructor(props) {
    super(props)
    this.state = { openSections: { parentMaterials: false, fillerMaterials: false, weldingParameters: false } }
  }

  toggle(key) {
    this.setState(prev => ({ openSections: { ...prev.openSections, [key]: !prev.openSections[key] } }))
  }

  render() {
    const { wps } = this.props
    const { openSections } = this.state
    return (
      <div>
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <strong style={{ fontSize: '15px' }}>WELDING PROCEDURE SPECIFICATION (WPS)</strong>
          <div style={{ fontSize: '13px' }}>{wps.standard && wps.standard.name}</div>
        </div>

        <div style={s.row}>
          <Field label="No" value={wps.no} />
          <Field label="Rev No" value={wps.rev_no} />
          <Field label="Manufacturer" value={wps.manufacturer} />
          <Field label="Release date" value={wps.release_date} />
          <Field label="Actual" value={wps.actual ? 'YES' : 'NO'} />
          <Field label="PQR/WPQR/WPQT" value={wps.pqr_wpqr_wpqt && wps.pqr_wpqr_wpqt.join(', ')} />
        </div>

        <div style={s.row}>
          <Field label="Joint types" value={wps.joint_types_names} />
          <Field label="Weld types" value={wps.weld_types_names} />
          <Field label="Welding positions" value={wps.welding_positions_names} />
          <Field label="Metal transfer" value={wps.metal_transfers_names} />
          <Field label="Preparation" value={wps.preparations_names} />
          <Field label="Welding details" value={wps.welding_details_names} />
          <Field label="Shield gas" value={wps.shield_gases_names} />
          <Field label="Shield gas flow rate" value={wps.shield_gas_flow_rate} />
          <Field label="Backing gas" value={wps.backing_gases_names} />
          <Field label="Backing gas flow rate" value={wps.backing_gas_flow_rate} />
          <Field label="Gouging" value={wps.gouging && wps.gouging.join(', ')} />
          <Field label="Bevel" value={wps.bevel && wps.bevel.join(', ')} />
          <Field label="Backing" value={wps.backing && wps.backing.join(', ')} />
        </div>

        <div style={s.row}>
          <Field label="Preheat temp (min)" value={wps.preheat_temp} />
          <Field label="Interpass temp (max)" value={wps.interpass_temp} />
          <Field label="Post weld heat operate" value={wps.post_weld_heat_operate} />
          <Field label="Post heating" value={wps.post_heating} />
          <Field label="Preheat maintenance temp" value={wps.preheat_maintenenace_temp} />
        </div>

        {wps.used_base_materials && wps.used_base_materials.length > 0 && (
          <div>
            <div style={s.sectionToggle} onClick={() => this.toggle('parentMaterials')}>
              <i className={`fa fa-chevron-${openSections.parentMaterials ? 'down' : 'right'}`} style={{ marginRight: '6px' }} />
              PARENT MATERIALS
            </div>
            {openSections.parentMaterials && (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Symbol</th>
                      <th style={s.th}>Name/Grade</th>
                      <th style={s.th}>Group</th>
                      <th style={s.th}>Standard</th>
                      <th style={s.th}>Thickness</th>
                      <th style={s.th}>Diameter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wps.used_base_materials.map((m, i) => (
                      <tr key={i}>
                        <td style={s.td}>{m.drawing_symbol || `t${i > 0 ? i : ''}`}</td>
                        <td style={s.td}>{m.base_material_designation_name}</td>
                        <td style={s.td}>{m.base_material && (m.base_material.subgroup || m.base_material.group)}</td>
                        <td style={s.td}>{m.base_material && m.base_material.standard}</td>
                        <td style={s.td}>{formatArrayRange(m.thickness)}</td>
                        <td style={s.td}>{formatArrayRange(m.diameter)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {wps.parameters && wps.parameters.length > 0 && (
          <div>
            <div style={s.sectionToggle} onClick={() => this.toggle('fillerMaterials')}>
              <i className={`fa fa-chevron-${openSections.fillerMaterials ? 'down' : 'right'}`} style={{ marginRight: '6px' }} />
              FILLER MATERIALS
            </div>
            {openSections.fillerMaterials && (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Run</th>
                      <th style={s.th}>Filler material</th>
                      <th style={s.th}>Classification</th>
                      <th style={s.th}>Standard</th>
                      <th style={s.th}>Group</th>
                      <th style={s.th}>Operate</th>
                      <th style={s.th}>Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wps.parameters.map((p, i) => {
                      const f = parseFillerMaterial(p.filler_material && p.filler_material.name)
                      return (
                        <tr key={i}>
                          <td style={s.td}>{p.run}</td>
                          <td style={s.td}>{f.material}</td>
                          <td style={s.td}>{f.classification}</td>
                          <td style={s.td}>{f.standard}</td>
                          <td style={s.td}>{p.filler_material && p.filler_material.standard && p.filler_material.standard.description}</td>
                          <td style={s.td}>{p.filler_material_operate}</td>
                          <td style={s.td}>{p.filler_material_size}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={s.sectionToggle} onClick={() => this.toggle('weldingParameters')}>
              <i className={`fa fa-chevron-${openSections.weldingParameters ? 'down' : 'right'}`} style={{ marginRight: '6px' }} />
              WELDING PARAMETERS
            </div>
            {openSections.weldingParameters && (
              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Run</th>
                      <th style={s.th}>Process</th>
                      <th style={s.th}>Position</th>
                      <th style={s.th}>Filler</th>
                      <th style={s.th}>Current [A]</th>
                      <th style={s.th}>Voltage [V]</th>
                      <th style={s.th}>Wire feed [m/min]</th>
                      <th style={s.th}>Travel speed [cm/min]</th>
                      <th style={s.th}>Polarity</th>
                      <th style={s.th}>Heat input [kJ/cm]</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wps.parameters.map((p, i) => {
                      const f = parseFillerMaterial(p.filler_material && p.filler_material.name)
                      return (
                        <tr key={i}>
                          <td style={s.td}>{p.run}</td>
                          <td style={s.td}>{p.welding_process && p.welding_process.name}</td>
                          <td style={s.td}>{p.welding_positions && p.welding_positions.map(pos => pos.name).join(', ')}</td>
                          <td style={s.td}>{f.material}</td>
                          <td style={s.td}>{formatRange(p.current)}</td>
                          <td style={s.td}>{formatRange(p.voltage)}</td>
                          <td style={s.td}>{formatRange(p.wire_feed)}</td>
                          <td style={s.td}>{formatRange(p.travel_speed)}</td>
                          <td style={s.td}>{p.type_of_current_polarity && p.type_of_current_polarity.name}</td>
                          <td style={s.td}>{formatRange(p.heat_input)}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {(wps.joint_design || wps.welding_sequence) && (
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '8px' }}>
            {wps.joint_design && wps.joint_design.url && (
              <div style={{ flex: '1 1 200px' }}>
                <div style={s.section}>Joint Design</div>
                <img src={API_BASE_URL + wps.joint_design.url} alt="Joint Design" style={{ maxWidth: '100%' }} />
              </div>
            )}
            {wps.welding_sequence && wps.welding_sequence.url && (
              <div style={{ flex: '1 1 200px' }}>
                <div style={s.section}>Welding Sequence</div>
                <img src={API_BASE_URL + wps.welding_sequence.url} alt="Welding Sequence" style={{ maxWidth: '100%' }} />
              </div>
            )}
          </div>
        )}

        {wps.additional_info && (
          <div>
            <div style={s.section}>ADDITIONAL INFORMATION</div>
            <p style={{ fontSize: '12px' }}>{wps.additional_info}</p>
          </div>
        )}

        {wps.attachments && wps.attachments.length > 0 && (
          <div>
            <div style={s.section}>ATTACHMENTS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
              {wps.attachments.map(att => {
                const url = API_BASE_URL + att.url
                const name = att.data_file_name || ''
                const isPdf = name.toLowerCase().endsWith('.pdf')
                const isImage = /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name)
                return (
                  <div key={att.id}>
                    <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{name}</div>
                    {isImage && (
                      <img src={url} alt={name} style={{ maxWidth: '100%', border: '1px solid #ddd' }} />
                    )}
                    {isPdf && (
                      <iframe src={url} title={name} style={{ width: '100%', height: '600px', border: '1px solid #ddd' }} />
                    )}
                    {!isImage && !isPdf && (
                      <a href={url} target="_blank" rel="noopener noreferrer">{name}</a>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }
}

export default WpsViewModal



// WEBPACK FOOTER //
// ./src/components/Welds/helpers/wps/WpsViewModal.js